<template>
    <div id="download-lut">
        <div id="app-layout-right-column__content__title">
            <span>Download a LUT</span>
        </div>

        <PlanUpgradeNotificationInSidebar v-if="isCurrentPlanFree" />
        <div v-else class="py-5">
            <label class="mb-2" for="name" theme="--start">LUT Name</label>
            <div name="export-lut-name-container">
                <v-text-field
                    rounded
                    v-model="$v.name.$model"
                    :disabled="!canEnterName"
                    name="export-lut-name"
                    ref="name"
                    @input="hideEnterNameTip()"
                    placeholder="Eg. My LUT #5"
                />
                <tippy theme="info" to="export-lut-name-container" v-if="!canEnterName">LUT name will bear the
                    respective {{ assetType }} name
                </tippy>
                <tippy theme="--start" to="export-lut-name" v-if="tips.requiredName" :visible="true" trigger="manual">
                    Field is required
                </tippy>
            </div>

            <v-expansion-panels accordion v-model="panel" multiple class="description-container">
                <v-expansion-panel>
                    <v-expansion-panel-header>
                        <div class="btn-advanced-options">
                            <span>Metadata (description, etc.) - optional</span>
                        </div>
                    </v-expansion-panel-header>
                    <v-expansion-panel-content>
                        <v-textarea
                            rounded
                            v-model="description"
                            name="export-lut-description"
                            placeholder="Any metadata you want to include in the LUT file"
                        />
                    </v-expansion-panel-content>
                </v-expansion-panel>
            </v-expansion-panels>

            <div class="option-container mb-3" :class="{'--disabled': !canExportACES}">
                <div class="switch switch-container">
                    <input type="checkbox" v-model="ACESinput" name="ACES-input" />
                    <span class="slider round"></span>
                </div>
                <span class="option-title">Bake-in ACES Input Transform</span>
                <i class="far fa-question-circle" name="info-1"></i>
                <tippy to="info-1" theme="info" :delay="[0, 0]">
                    This option allows you to include or exclude the selected ACES Input and Output Device Transforms in
                    the downloaded LUT. If you are
                    familiar with ACES workflow and your software supports it, we recommend disabling both options and
                    setting your internal ACES colour
                    management to ACEScct. Otherwise we recommend leaving both options on.
                </tippy>
            </div>

            <div class="option-container mb-7" :class="{'--disabled': !canExportACES}">
                <div class="switch switch-container">
                    <input type="checkbox" v-model="ACESoutput" name="ACES-output" />
                    <span class="slider round"></span>
                </div>
                <span class="option-title">Bake-in ACES Output Transform</span>
                <i class="far fa-question-circle" name="info-2"></i>
                <tippy to="info-2" theme="info" :delay="[0, 0]">
                    This option allows you to include or exclude the selected ACES Input and Output Device Transforms in
                    the downloaded LUT. If you are
                    familiar with ACES workflow and your software supports it, we recommend disabling both options and
                    setting your internal ACES colour
                    management to ACEScct. Otherwise we recommend leaving both options on.
                </tippy>
            </div>

            <v-expansion-panels accordion>
                <v-expansion-panel>
                    <v-expansion-panel-header>
                        <div class="btn-advanced-options">
                            <span>Additional download options</span>
                        </div>
                    </v-expansion-panel-header>
                    <v-expansion-panel-content>
                        <div id="advanced-export-options">
                            <div class="option-container mb-3">
                                <div class="switch switch-container">
                                    <input type="checkbox" v-model="IPP2" name="IPP2-export" />
                                    <span class="slider round"></span>
                                </div>
                                <span class="option-title">Download RED IPP2 LUT</span>
                                <i class="far fa-question-circle" name="info-3"></i>
                                <tippy to="info-3" theme="info" :delay="[0, 0]">
                                    This option allows you to export a LUT tailored for RED's IPP2 workflow for use
                                    in-camera for monitoring purposes. You
                                    should not use this LUT in post.
                                </tippy>
                            </div>

                            <div class="option-container mb-3">
                                <div class="switch switch-container">
                                    <input type="checkbox" v-model="vlt" name="vlt-export" />
                                    <span class="slider round"></span>
                                </div>
                                <span class="option-title">Download Panasonic .vlt LUT</span>
                                <i class="far fa-question-circle" name="info-4"></i>
                                <tippy to="info-4" theme="info" :delay="[0, 0]">
                                    This option allows you to export a LUT in Panasonic's .vlt format for use in-camera
                                    for
                                    monitoring purposes. When using the LUT in-camera, your camera must be set to
                                    V-Gamut
                                    and V-Log. You should not use this LUT in post.
                                </tippy>
                            </div>

                            <div class="option-container mb-3">
                                <div class="switch switch-container">
                                    <input type="checkbox" v-model="davinci" name="davinci-export" />
                                    <span class="slider round"></span>
                                </div>
                                <span class="option-title">Download DaVinci Wide Gamut LUT</span>
                                <i class="far fa-question-circle" name="info-5"></i>
                                <tippy to="info-5" theme="info" :delay="[0, 0]">
                                    This option allows you to export a LUT tailored for the DaVinci Wide Gamut
                                    Intermediate
                                    colour managed workflow. When using this LUT in post, make sure that you setup your
                                    workflow as DaVinci YRGB Color Managed
                                </tippy>
                            </div>

                            <div class="option-container mb-3">
                                <div class="switch switch-container">
                                    <input type="checkbox" v-model="logc4" name="logc4-export" />
                                    <span class="slider round"></span>
                                </div>
                                <span class="option-title">Download Arri LogC4 LUT</span>
                                <i class="far fa-question-circle" name="info-6"></i>
                                <tippy to="info-6" theme="info" :delay="[0, 0]">
                                    This option allows you to export a LUT tailored for the Arri LogC4 colour space.
                                    This
                                    LUT can be used in the Arri Reference Tool to create an AML file that can be loaded
                                    in
                                    the Arri Alexa 35 camera.
                                </tippy>
                            </div>
                        </div>
                    </v-expansion-panel-content>
                </v-expansion-panel>
            </v-expansion-panels>

            <div class="buttons-container">
                <button type="button" class="button button--primary" @click="onSubmit" :disabled="exporting">
                    <v-progress-circular v-if="exporting" indeterminate color="var(--color-white)" size="18"
                                         width="3"></v-progress-circular>
                    <span v-else class="font-weight-bold">Export</span>
                </button>
            </div>
        </div>
    </div>
</template>

<script>
import { required } from "vuelidate/lib/validators";
import { mapGetters, mapState } from "vuex";
import JSZip from "jszip";
import download from "downloadjs";
import PlanUpgradeNotificationInSidebar from "@/components/app/ui/PlanUpgradeNotificationInSidebar.vue";

export default {
    components: {PlanUpgradeNotificationInSidebar},
    data() {
        return {
            name: "",
            description: "",
            ACESinput: true,
            ACESoutput: true,
            IPP2: 0,
            vlt: 0,
            davinci: 0,
            logc4: false,
            exporting: false,
            tips: {
                requiredName: false,
            },
            panel: [0]
        };
    },
    validations: {
        name: {required},
    },
    computed: {
        canExportACES() {
            return ![
                this.vlt,
                this.IPP2,
                this.davinci,
                this.logc4
            ].includes(true)
        },
        canEnterName() {
            if (this.action.action == "export-lut-from-asset" && this.action.data.length > 1) return false;
            else return true;
        },
        assetType() {
            let type = this.action.type == "candidate" ? "Candidate" : "Match";
            return type;
        },
        ...mapState("Projects", ["action"]),
        ...mapState("UI", ["rightColumn"]),
        ...mapGetters('User', ['isCurrentPlanFree']),
    },
    watch: {
        action(newValue) {
            if (this.action.action == "export-lut-from-asset" && this.action.data.length == 1) {
                this.name = newValue.data[0].nicename;
            }
        },

        rightColumn(newValue) {
            if (newValue == 'download-lut') {
                this.$refs.name.focus();
            }
        }

    },
    methods: {
        hideEnterNameTip() {
            this.tips.requiredName = false;
        },
        async onSubmit() {
            if (this.$v.$invalid && this.canEnterName) {
                this.tips.requiredName = true;
                return;
            }

            this.tips.requiredName = false;
            this.exporting = true;

            if (this.name == "") this.name = "LUT-" + Date.now();

            let data = {
                name: this.name,
                description: this.description,
                options: {
                    ACESinput: this.ACESinput,
                    ACESoutput: this.ACESoutput,
                    IPP2: this.IPP2,
                    vlt: this.vlt,
                    davinci: this.davinci,
                    logc4: this.logc4
                },
            };

            if (this.action.action == "export-lut-from-asset") {
                await window.lutify.storeLastState();

                let exportLutFromAssets = [];
                let selectedAssets = this.action.data;
                window.lutify.exportingLut = true; // to not switch between tabs on right

                for await (const asset of selectedAssets) {
                    await window.lutify.applyCompare(asset.blocks, "waitToLoad", false);
                    let name = selectedAssets.length == 1 ? data.name : asset.nicename;
                    let lutFiles = await window.luts.prepareDownloadLuts({...data, name});

                    exportLutFromAssets.push({
                        folderName: asset.nicename,
                        luts: lutFiles,
                    });
                }

                this.downloadLuts(exportLutFromAssets);
                await window.lutify.applyLastState(this.$store.state.Projects.image);

                window.lutify.exportingLut = false;

                await this.$store.dispatch("Projects/setAction", {
                    type: null,
                    action: "export-lut-from-asset-complete",
                    data: null,
                });
            }

            // export for current image
            else {
                let lutFiles = await window.luts.prepareDownloadLuts(data);
                this.downloadLuts([
                    {
                        luts: lutFiles,
                    },
                ]);
            }

            this.exporting = false;
            window.UI.hideModal();
            this.name = "";
            this.description = "";
        },

        downloadLuts(data) {
            let zip = new JSZip();

            // 1 "folder" / 1 lut
            if (data.length == 1 && data[0].luts.length == 1) {
                download(data[0].luts[0], data[0].luts[0].name);
            } else {
                let folderNamesList = [];
                let folderPath;

                data.forEach(folder => {
                    if (folderNamesList.includes(folder.folderName)) {
                        for (let i = 1; i < 100; i++) {
                            if (!folderNamesList.includes(`${folder.folderName}-${i}`)) {
                                folderPath = `${folder.folderName}-${i}/`;
                                folderNamesList.push(`${folder.folderName}-${i}`);
                                break;
                            }
                        }
                    } else {
                        folderPath = `${folder.folderName}/`;
                        folderNamesList.push(folder.folderName);
                    }

                    folder.luts.forEach(lut => {
                        let path;

                        // when we export from multiple assets
                        if (data.length > 1) path = folderPath + lut.name;
                        // when exporting from 1 asset, but several luts
                        else path = lut.name;

                        zip.file(path, lut);
                    });
                });
                zip.generateAsync({type: "blob"}).then(blob => download(blob, `LUT-export-${Date.now()}.zip`));
            }
        },
    },
}
</script>

<style lang="scss">
#download-lut {
    .switch-container {
        margin-left: initial;
    }

    label {
        //margin-bottom: 10px !important;
        line-height: 1;
        display: block;
        height: auto;
        padding: 0;
        font-size: 1rem;
    }

    .v-input {
        padding: 0;
        margin: 0;
    }

    .v-input__slot {
        padding: 0;
    }

    input[type="text"],
    textarea {
        line-height: 20px;
        background: white;
        -webkit-appearance: none;
        border-radius: 5px;
        border: none;
        color: var(--color-bg-dark);
        padding: 7px 10px;
        //margin-bottom: 20px;
        font-size: 1rem;

        &::placeholder {
            color: var(--font-default-color);
            font-family: Poppins;
            font-size: 1rem;
        }
    }

    textarea {
        resize: none;
        height: 60px;
        width: 100%;
    }

    input[type="submit"],
    button {
        margin: 0 auto;
        outline: 0;

        &:hover {
            opacity: 0.8;
        }
    }

    .option-container {
        display: flex;
        align-items: center;
        gap: 1rem;

        &.--disabled {
            opacity: 0.25;

            input {
                pointer-events: none;
                cursor: not-allowed;
            }
        }

        .option-title {
            line-height: 1.25;
        }
    }

    .btn-advanced-options {
        margin-bottom: 0;

        > span {
            line-height: 20px;
        }
    }

    .description-container {
        margin-bottom: 8px;

        .v-expansion-panel-header {
            span {
                line-height: 12px;
            }
        }
    }

    div[name="export-lut-name-container"] {
        input {
            background: white;
            border-radius: 5px;
            border: none;
            color: #121212;
            padding: 7px 10px;
            font-size: 1rem;
            width: 100%;

            &:disabled {
                opacity: 0.3;
                cursor: not-allowed;
            }
        }
    }

    .buttons-container {
        margin-top: 30px;
        display: flex;
        justify-content: center;
    }

    .v-expansion-panel {
        background: transparent;
        color: inherit;

        &:before {
            display: none;
        }

        .v-expansion-panel-header {
            padding: 0;
            min-height: auto;
            margin-bottom: 10px;

            &:before {
                display: none;
            }

            &__icon {
                margin-left: 5px;

                i {
                    font-size: 18px;
                    color: var(--font-default-color);
                }
            }

            & > :not(&__icon) {
                flex: initial;
            }
        }

        .v-expansion-panel-content__wrap {
            padding: 0;
        }
    }
}
</style>